import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon, FilterIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, ChevronDownIcon } from 'lucide-react';
export function ApplicationsTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  // Mock data for applications
  const applications = [{
    id: 'APP-2025-0001',
    borrower: 'Lucas Scheer',
    coBorrowers: [],
    status: 'complete',
    dateCreated: '2025-01-15',
    dateUpdated: '2025-01-31',
    assignedTo: 'John Broker',
    propertyAddress: '123 Main St, New York, NY',
    loanAmount: 450000,
    downPayment: 90000
  }, {
    id: 'APP-2025-0002',
    borrower: 'Sarah Johnson',
    coBorrowers: ['Michael Johnson'],
    status: 'in_progress',
    dateCreated: '2025-01-20',
    dateUpdated: '2025-01-28',
    assignedTo: 'John Broker',
    propertyAddress: '456 Park Ave, Boston, MA',
    loanAmount: 380000,
    downPayment: 76000
  }, {
    id: 'APP-2025-0003',
    borrower: 'David Chen',
    coBorrowers: ['Lisa Chen'],
    status: 'pending',
    dateCreated: '2025-01-25',
    dateUpdated: '2025-01-27',
    assignedTo: 'Jane Agent',
    propertyAddress: '789 Oak St, Chicago, IL',
    loanAmount: 320000,
    downPayment: null
  }, {
    id: 'APP-2025-0004',
    borrower: 'Emily Rodriguez',
    coBorrowers: [],
    status: 'complete',
    dateCreated: '2025-01-10',
    dateUpdated: '2025-01-26',
    assignedTo: 'John Broker',
    propertyAddress: '101 Pine St, Miami, FL',
    loanAmount: 520000,
    downPayment: 104000
  }, {
    id: 'APP-2025-0005',
    borrower: 'Robert Taylor',
    coBorrowers: ['Lisa Taylor'],
    status: 'in_progress',
    dateCreated: '2025-01-22',
    dateUpdated: '2025-01-24',
    assignedTo: 'Jane Agent',
    propertyAddress: '222 Maple Dr, Seattle, WA',
    loanAmount: 495000,
    downPayment: 99000
  }];
  // Filter applications based on search term and status
  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.borrower.toLowerCase().includes(searchTerm.toLowerCase()) || app.id.toLowerCase().includes(searchTerm.toLowerCase()) || app.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Sort applications based on sort criteria
  const sortedApplications = [...filteredApplications].sort((a, b) => {
    if (sortBy === 'date') {
      return sortOrder === 'asc' ? new Date(a.dateUpdated).getTime() - new Date(b.dateUpdated).getTime() : new Date(b.dateUpdated).getTime() - new Date(a.dateUpdated).getTime();
    } else if (sortBy === 'borrower') {
      return sortOrder === 'asc' ? a.borrower.localeCompare(b.borrower) : b.borrower.localeCompare(a.borrower);
    } else if (sortBy === 'loanAmount') {
      return sortOrder === 'asc' ? a.loanAmount - b.loanAmount : b.loanAmount - a.loanAmount;
    }
    return 0;
  });
  // Status badge component
  const StatusBadge = ({
    status
  }) => {
    switch (status) {
      case 'complete':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800" data-id="element-1062">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-1063" />
            Complete
          </span>;
      case 'in_progress':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800" data-id="element-1064">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-1065" />
            In Progress
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800" data-id="element-1066">
            <AlertCircleIcon className="mr-1 h-3 w-3" data-id="element-1067" />
            Pending
          </span>;
      default:
        return null;
    }
  };
  return <div className="space-y-4" data-id="element-1068">
      <div className="flex items-center justify-between" data-id="element-1069">
        <h1 className="text-2xl font-bold text-neutral-900" data-id="element-1070">Applications</h1>
        <Link to="/applications/new" className="btn btn-primary" data-id="element-1071">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-1072" />
          New Application
        </Link>
      </div>
      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row justify-between space-y-3 md:space-y-0 md:space-x-4" data-id="element-1073">
        <div className="relative max-w-md w-full" data-id="element-1074">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1075">
            <SearchIcon className="h-5 w-5 text-neutral-400" data-id="element-1076" />
          </div>
          <input type="text" className="input pl-10 w-full" placeholder="Search by borrower, ID, or address..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1077" />
        </div>
        <div className="flex space-x-3" data-id="element-1078">
          <div className="relative" data-id="element-1079">
            <select className="select pr-10 appearance-none" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-1080">
              <option value="all" data-id="element-1081">All Statuses</option>
              <option value="complete" data-id="element-1082">Complete</option>
              <option value="in_progress" data-id="element-1083">In Progress</option>
              <option value="pending" data-id="element-1084">Pending</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral-500" data-id="element-1085">
              <ChevronDownIcon className="h-4 w-4" data-id="element-1086" />
            </div>
          </div>
          <button className="btn btn-outline" data-id="element-1087">
            <FilterIcon className="mr-2 h-4 w-4" data-id="element-1088" />
            More Filters
          </button>
        </div>
      </div>
      {/* Applications Table */}
      <div className="table-container" data-id="element-1089">
        <table className="table" data-id="element-1090">
          <thead className="table-header" data-id="element-1091">
            <tr data-id="element-1092">
              <th className="table-header-cell" data-id="element-1093">Application ID</th>
              <th className="table-header-cell" data-id="element-1094">Borrower</th>
              <th className="table-header-cell" data-id="element-1095">Property Address</th>
              <th className="table-header-cell" data-id="element-1096">Loan Amount</th>
              <th className="table-header-cell" data-id="element-1097">Down Payment</th>
              <th className="table-header-cell" data-id="element-1098">Status</th>
              <th className="table-header-cell" data-id="element-1099">Last Updated</th>
              <th className="table-header-cell" data-id="element-1100">Assigned To</th>
            </tr>
          </thead>
          <tbody className="table-body" data-id="element-1101">
            {sortedApplications.map(app => <tr key={app.id} className="table-row" data-id="element-1102">
                <td className="table-cell" data-id="element-1103">
                  <Link to={`/applications/${app.id}`} className="text-primary-600 hover:text-primary-800 font-medium" data-id="element-1104">
                    {app.id}
                  </Link>
                </td>
                <td className="table-cell" data-id="element-1105">
                  {app.borrower}
                  {app.coBorrowers.length > 0 && <span className="text-neutral-500 text-sm ml-1" data-id="element-1106">
                      +{app.coBorrowers.length}
                    </span>}
                </td>
                <td className="table-cell" data-id="element-1107">{app.propertyAddress}</td>
                <td className="table-cell" data-id="element-1108">
                  ${app.loanAmount.toLocaleString()}
                </td>
                <td className="table-cell" data-id="element-1109">
                  {app.downPayment ? `$${app.downPayment.toLocaleString()} (${Math.round(app.downPayment / app.loanAmount * 100)}%)` : 'Not specified'}
                </td>
                <td className="table-cell" data-id="element-1110">
                  <StatusBadge status={app.status} data-id="element-1111" />
                </td>
                <td className="table-cell" data-id="element-1112">
                  {new Date(app.dateUpdated).toLocaleDateString()}
                </td>
                <td className="table-cell" data-id="element-1113">{app.assignedTo}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}